import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { HelpArticle, HelpCategory } from "@/hooks/useHelpCenter";

export interface SearchResult {
  article: HelpArticle & { category: HelpCategory };
  matchType: "title" | "summary" | "content";
  snippet: string;
  score: number;
}

// Extract text content from article body (handles both JSON blocks and legacy HTML)
function extractTextContent(body: string): string {
  if (!body) return "";
  
  try {
    const content = JSON.parse(body);
    if (content?.blocks && Array.isArray(content.blocks)) {
      return content.blocks
        .map((block: { content?: string; title?: string }) => 
          [block.content, block.title].filter(Boolean).join(" ")
        )
        .join(" ");
    }
  } catch {
    // Legacy HTML content - strip tags
    return body.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
  
  return "";
}

// Generate a snippet around the matching text
function generateSnippet(text: string, query: string, maxLength: number = 150): string {
  if (!text || !query) return "";
  
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);
  
  if (index === -1) return text.substring(0, maxLength) + (text.length > maxLength ? "..." : "");
  
  // Calculate start position (try to show context before match)
  const contextBefore = 40;
  let start = Math.max(0, index - contextBefore);
  
  // Adjust to start at word boundary
  if (start > 0) {
    const spaceIndex = text.indexOf(" ", start);
    if (spaceIndex !== -1 && spaceIndex < index) {
      start = spaceIndex + 1;
    }
  }
  
  // Calculate end position
  let end = Math.min(text.length, start + maxLength);
  
  // Adjust to end at word boundary
  if (end < text.length) {
    const spaceIndex = text.lastIndexOf(" ", end);
    if (spaceIndex > start + maxLength - 30) {
      end = spaceIndex;
    }
  }
  
  let snippet = text.substring(start, end);
  if (start > 0) snippet = "..." + snippet;
  if (end < text.length) snippet = snippet + "...";
  
  return snippet;
}

// Calculate relevance score
function calculateScore(article: HelpArticle, query: string): { score: number; matchType: "title" | "summary" | "content"; snippet: string } {
  const lowerQuery = query.toLowerCase();
  const title = article.title?.toLowerCase() || "";
  const summary = article.summary?.toLowerCase() || "";
  const content = extractTextContent(article.body).toLowerCase();
  
  // Title exact match (highest priority)
  if (title === lowerQuery) {
    return { score: 100, matchType: "title", snippet: article.summary || "" };
  }
  
  // Title starts with query
  if (title.startsWith(lowerQuery)) {
    return { score: 90, matchType: "title", snippet: article.summary || "" };
  }
  
  // Title contains query
  if (title.includes(lowerQuery)) {
    return { score: 80, matchType: "title", snippet: article.summary || "" };
  }
  
  // Summary contains query
  if (summary.includes(lowerQuery)) {
    return { 
      score: 60, 
      matchType: "summary", 
      snippet: generateSnippet(article.summary || "", query) 
    };
  }
  
  // Content contains query
  if (content.includes(lowerQuery)) {
    return { 
      score: 40, 
      matchType: "content", 
      snippet: generateSnippet(extractTextContent(article.body), query) 
    };
  }
  
  return { score: 0, matchType: "content", snippet: "" };
}

// Custom hook for debounced value
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Main search hook
export function useHelpSearch(query: string, enabled: boolean = true) {
  const debouncedQuery = useDebounce(query.trim(), 300);
  
  return useQuery({
    queryKey: ["help-search", debouncedQuery],
    queryFn: async (): Promise<SearchResult[]> => {
      if (!debouncedQuery || debouncedQuery.length < 2) return [];
      
      // Fetch all published articles with categories
      const { data, error } = await supabase
        .from("help_articles")
        .select(`
          *,
          category:help_categories(*)
        `)
        .eq("is_published", true)
        .eq("is_archived", false);
      
      if (error) throw error;
      if (!data) return [];
      
      // Filter and score articles
      const results: SearchResult[] = [];
      
      for (const article of data) {
        const { score, matchType, snippet } = calculateScore(article as HelpArticle, debouncedQuery);
        
        if (score > 0) {
          results.push({
            article: article as HelpArticle & { category: HelpCategory },
            matchType,
            snippet,
            score,
          });
        }
      }
      
      // Sort by score (descending) and limit to 20 results
      return results
        .sort((a, b) => b.score - a.score)
        .slice(0, 20);
    },
    enabled: enabled && debouncedQuery.length >= 2,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

// Group search results by category
export function useGroupedSearchResults(results: SearchResult[]) {
  return useMemo(() => {
    const grouped: Record<string, { category: HelpCategory; results: SearchResult[] }> = {};
    
    for (const result of results) {
      const categoryId = result.article.category?.id || "uncategorized";
      
      if (!grouped[categoryId]) {
        grouped[categoryId] = {
          category: result.article.category,
          results: [],
        };
      }
      
      grouped[categoryId].results.push(result);
    }
    
    // Sort categories by their highest-scoring result
    return Object.values(grouped).sort((a, b) => {
      const maxScoreA = Math.max(...a.results.map(r => r.score));
      const maxScoreB = Math.max(...b.results.map(r => r.score));
      return maxScoreB - maxScoreA;
    });
  }, [results]);
}
